/**
 * 2-D Kalman filter for GPS lat/lng smoothing.
 *
 * Models position with a constant-velocity dynamic. Each axis (lat, lng) is
 * filtered independently because, over the small geographic distances of a
 * single ride, lat and lng noise are effectively independent.
 *
 * Tuning knobs:
 *   processNoise (Q)   – how much we expect the user to genuinely move
 *                        between samples. Higher = follows movement faster
 *                        but lets in more jitter.
 *   measurementNoise   – derived from each GPS fix's `accuracy` (meters).
 *
 * Reset semantics: a long gap (>15s) or huge jump (>5km) zeros the filter
 * state and re-initializes from the new sample — handles tunnels, app
 * resumes, etc.
 */
export class GPSKalmanFilter {
  constructor({ processNoise = 1.0 } = {}) {
    this.Q = processNoise; // process variance (m^2/s)
    this.lat = null;
    this.lng = null;
    this.varLat = 1;
    this.varLng = 1;
    this.tsMs = 0;
  }

  reset() {
    this.lat = null; this.lng = null;
    this.varLat = 1; this.varLng = 1;
    this.tsMs = 0;
  }

  /**
   * Feed in a raw GPS fix.
   *   accuracyMeters → measurement variance ≈ accuracy^2 / 4 (95% conf).
   * Returns the smoothed { lat, lng }.
   */
  update({ lat, lng, accuracy = 30, tsMs = Date.now() }) {
    if (this.lat === null) {
      this.lat = lat; this.lng = lng;
      this.varLat = (accuracy * accuracy) / 4;
      this.varLng = this.varLat;
      this.tsMs = tsMs;
      return { lat, lng };
    }
    const dt = Math.max(0.001, (tsMs - this.tsMs) / 1000);
    // Sanity: huge gap or huge jump → snap. ~5km or 15s.
    const haver = approxMeters(this.lat, this.lng, lat, lng);
    if (dt > 15 || haver > 5000) {
      this.lat = lat; this.lng = lng;
      this.varLat = (accuracy * accuracy) / 4;
      this.varLng = this.varLat;
      this.tsMs = tsMs;
      return { lat, lng };
    }
    // Predict step — variance grows by Q*dt.
    this.varLat += this.Q * dt;
    this.varLng += this.Q * dt;
    // Measurement variance from reported accuracy (degrees → meters approx).
    const r = (accuracy * accuracy) / 4;
    // Convert accuracy meters → degrees for axis-aware blending. For small
    // areas 1° lat ≈ 111_000m and 1° lng ≈ 111_000*cos(lat). We work in
    // degree-space here because the filter state IS lat/lng degrees; the
    // variance just controls the blending weight K, not the unit.
    const KLat = this.varLat / (this.varLat + r);
    const KLng = this.varLng / (this.varLng + r);
    this.lat = this.lat + KLat * (lat - this.lat);
    this.lng = this.lng + KLng * (lng - this.lng);
    this.varLat *= 1 - KLat;
    this.varLng *= 1 - KLng;
    this.tsMs = tsMs;
    return { lat: this.lat, lng: this.lng };
  }
}

// Cheap great-circle distance (meters). Plenty accurate for jitter-rejection.
function approxMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}
